import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  updateWorklogStatus,
  useAddWorkLogMutation,
  useUpdateWorkLogMutation,
  useBulkAddWorkLogMutation,
  updateWorklogItem,
  addWorklogItems,
} from '@/features/worklogSlice';
import {
  closeWorklogModal,
  WorklogModalMode,
} from '@/features/worklogModalSlice';
import { analysisApi } from '@/features/analysisApi';
import { fieldApi } from '@/features/fieldSlice';
import { listApi } from '@/features/customListSlice';
import { captureException } from '@sentry/react';
import { notifications } from '@mantine/notifications';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import type {
  GroupAnalysisFilter,
  WorkLogResponse,
} from '@/generated/types/navigator/types.gen';
import { Check, X } from 'react-feather';
import { incrementMvtCacheCounter } from '@/features/analysisMapSlice';
import {} from '@/features/filterSlice';
import {
  getFilterQuery,
  addGroupToFilter,
  encodeFilterQuery,
} from '@/features/filterHelpers';

/**
 * Form data structure for worklog submission.
 * Extends WorkLogResponse but replaces date string with Date object for form handling.
 */
export interface WorklogFormProps extends Partial<
  Omit<WorkLogResponse, 'date'>
> {
  date: Date;
  list_id?: string;
}

interface SubmitParams {
  values: WorklogFormProps;
  worklogId?: string;
  isDirty: boolean;
}

/**
 * Custom hook to handle worklog submission (create and update operations).
 */
export const useWorklogSubmit = ({ planId }: { planId?: string } = {}) => {
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();
  const { mode, filter } = useAppSelector((s) => s.worklogModal);
  const { currentViewMode } = useAppSelector((s) => s.analysisMap);
  const { activeGroupName, selectedNetworkGroup } = useAppSelector(
    (s) => s.analysisMap
  );
  const [addWorklog, { isLoading: isAdding }] = useAddWorkLogMutation();
  const [updateWorklog, { isLoading: isUpdating }] = useUpdateWorkLogMutation();
  const [bulkAddWorklog, { isLoading: isBulkAdding }] =
    useBulkAddWorkLogMutation();
  const isLoading = isAdding || isUpdating || isBulkAdding;

  const submitWorklog = useCallback(
    async ({ values, worklogId, isDirty }: SubmitParams) => {
      if (!isDirty) {
        dispatch(closeWorklogModal());
        return;
      }

      // Validate that we don't have both filter and worklogId
      if (filter && worklogId) {
        captureException(
          new Error(
            'Cannot have both filter and worklogId in worklog submission'
          )
        );
        dispatch(closeWorklogModal());
        return;
      }

      const isEditMode = mode === WorklogModalMode.EDIT;
      const isBulkMode = mode === WorklogModalMode.BULK_ADD;
      const listId = currentViewMode.modeDetails?.id;

      const notificationId = notifications.show({
        loading: true,
        title: intl.formatMessage({
          id: isEditMode ? 'worklog.updating' : 'worklog.adding',
        }),
        message: intl.formatMessage({
          id: isEditMode ? 'worklog.updatingMessage' : 'worklog.addingMessage',
        }),
        autoClose: false,
        withCloseButton: false,
      });

      try {
        if (isEditMode) {
          if (!worklogId) {
            throw new Error('Worklog ID is required for updates');
          }

          const updated = await updateWorklog({
            body: values,
            id: worklogId,
          }).unwrap();

          dispatch(updateWorklogItem(updated));
        } else if (isBulkMode && filter) {
          let filterQuery: GroupAnalysisFilter;
          if (activeGroupName) {
            const filterWithAddedGroup = addGroupToFilter(
              activeGroupName,
              selectedNetworkGroup,
              filter
            );
            filterQuery = getFilterQuery(filterWithAddedGroup);
          } else {
            filterQuery = getFilterQuery(filter);
          }
          const encodedFilter = encodeFilterQuery(filterQuery);

          await bulkAddWorklog({
            filter_query: encodedFilter,
            date: values.date.toISOString(),
            entity_type: values.entity_type,
            status: values.status!,
            notes: values.notes,
            list_id: listId,
          }).unwrap();
        } else {
          const added = await addWorklog({
            ...values,
            plan_id: planId,
          }).unwrap();

          dispatch(addWorklogItems([added]));
        }
        // Force refresh of map tiles
        dispatch(incrementMvtCacheCounter());

        // Invalidate all tags aggressively to ensure data is refreshed everywhere
        dispatch(fieldApi.util.invalidateTags([{ type: 'fieldWorkStatus' }]));
        dispatch(
          analysisApi.util.invalidateTags([
            { type: 'groupItemData' },
            { type: 'itemData' },
            { type: 'analysisData' },
            { type: 'poleData' },
          ])
        );
        dispatch(listApi.util.invalidateTags([{ type: 'Lists' }]));

        notifications.update({
          id: notificationId,
          color: 'green',
          title: intl.formatMessage({
            id: isEditMode ? 'worklog.updateSuccess' : 'worklog.addSuccess',
          }),
          message: intl.formatMessage({
            id: isEditMode
              ? 'worklog.updateSuccessMessage'
              : 'worklog.addSuccessMessage',
          }),
          icon: <Check />,
          loading: false,
          autoClose: 2000,
        });

        dispatch(closeWorklogModal());
        dispatch(updateWorklogStatus(false));
      } catch (error) {
        captureException(error);

        notifications.update({
          id: notificationId,
          color: 'red',
          title: intl.formatMessage({ id: 'action.error' }),
          message: intl.formatMessage({ id: 'action.genericError' }),
          icon: <X />,
          loading: false,
          autoClose: 5000,
        });
      }
    },
    [
      filter,
      mode,
      intl,
      dispatch,
      updateWorklog,
      bulkAddWorklog,
      addWorklog,
      planId,
      currentViewMode.modeDetails?.id,
      activeGroupName,
      selectedNetworkGroup,
    ]
  );

  return {
    submitWorklog,
    isLoading,
  };
};
